import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Sidebar";
import DashboardHeader from "../../components/DashboardHeader";
import apiClient from "../../services/apiClient";
import {
  getPersonalTaxYears,
  getPersonalTaxSummary,
  getPersonalTaxTransactions,
  getPersonalTaxChecklist,
} from "../../services/api";
import "./PersonalTax.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CommonLoader from "../../components/CommonLoader";

const PersonalTaxPage = () => {
  const [taxYears, setTaxYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState("");
  const [selectedYearObj, setSelectedYearObj] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Business ID from Redux (matching other dashboard pages)
  const businessId = useSelector((state) => state?.user?.businessId);
  const businessData = useSelector((state) => state?.user?.businessData);

  // 1. Fetch available tax years on mount
  useEffect(() => {
    if (!businessId) return;

    const fetchYears = async () => {
      try {
        setIsLoading(true);
        // Using mock data fallback if API is not yet alive
        const response = await apiClient.get(
          `${getPersonalTaxYears}/${businessId}`
        );
        
        if (response.status === 200 && response.data && response.data.length > 0) {
          setTaxYears(response.data);
          setSelectedYearId(response.data[0]._id);
          setSelectedYearObj(response.data[0]);
        } else {
          // Fallback static data mapping if API fails/empty
          const defaultYears = [
            { id: "ty_2025_26", label: "2025/26", startDate: "6 Apr 2025", endDate: "5 Apr 2026", filingDeadline: "31 Jan 2027", paymentOnAccount: "31 Jul 2027" },
            { id: "ty_2024_25", label: "2024/25", startDate: "6 Apr 2024", endDate: "5 Apr 2025", filingDeadline: "31 Jan 2026", paymentOnAccount: "31 Jul 2026" }
          ];
          setTaxYears(defaultYears);
          setSelectedYearId(defaultYears[0].id);
          setSelectedYearObj(defaultYears[0]);
        }
      } catch (error) {
        console.error("Error fetching tax years", error);
        // Fallback static data mapping
        const defaultYears = [
          { id: "ty_2025_26", label: "2025/26", startDate: "6 Apr 2025", endDate: "5 Apr 2026", filingDeadline: "31 Jan 2027", paymentOnAccount: "31 Jul 2027" },
          { id: "ty_2024_25", label: "2024/25", startDate: "6 Apr 2024", endDate: "5 Apr 2025", filingDeadline: "31 Jan 2026", paymentOnAccount: "31 Jul 2026" }
        ];
        setTaxYears(defaultYears);
        setSelectedYearId(defaultYears[0].id);
        setSelectedYearObj(defaultYears[0]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchYears();
  }, [businessId]);

  // 2. Fetch specific year data when selectedYearId changes
  useEffect(() => {
    if (!businessId || !selectedYearId) return;

    const fetchTaxData = async () => {
      try {
        setIsLoading(true);
        const yearObj = taxYears.find(y => y.id === selectedYearId || y._id === selectedYearId);
        if (yearObj) setSelectedYearObj(yearObj);

        // Try getting real data
        const summaryRes = await apiClient.get(`${getPersonalTaxSummary}/${businessId}/${selectedYearId}/summary`).catch(() => null);
        const txRes = await apiClient.get(`${getPersonalTaxTransactions}/${businessId}/${selectedYearId}/transactions`).catch(() => null);
        const checkRes = await apiClient.get(`${getPersonalTaxChecklist}/${businessId}/${selectedYearId}/checklist`).catch(() => null);

        // Handle Summary
        if (summaryRes?.status === 200 && summaryRes.data) {
          setSummary(summaryRes.data);
        } else {
          // Fallback static data mapped to the UI mockup
          setSummary({
            paye: { salary: 42000, tax: 6300, nic: 3100 },
            dividends: { amount: 28500, allowanceRemaining: 500 },
            otherIncome: { amount: 3200, label: "Benefits & reimbursements" },
            estimatedTax: { total: 11750, split: 5875 }
          });
        }

        // Handle Transactions/Income Breakdown
        if (txRes?.status === 200 && txRes.data) {
          setTransactions(txRes.data);
        } else {
          // Fallback static data
          setTransactions([
            { id: 1, source: "PAYE salary", gross: 42000, taxDeducted: 6300, evidence: "View FPS", evidenceLink: "#", isLink: true },
            { id: 2, source: "Dividends", gross: 28500, taxDeducted: null, evidence: "8 vouchers", evidenceLink: "#", isLink: true },
            { id: 3, source: "Benefits in kind", gross: 1800, taxDeducted: null, evidence: "P11D", evidenceLink: "#", isLink: true },
          ]);
        }

        // Handle Checklist
        if (checkRes?.status === 200 && checkRes.data) {
          setChecklist(checkRes.data);
        } else {
          // Fallback static data
          setChecklist([
            { id: 1, text: "Upload dividend voucher for payment dated 12 Dec 2025.", btnText: null },
            { id: 2, text: "Confirm accuracy of mileage reimbursements (£1,200)", btnText: "Mark complete" },
            { id: 3, text: "Approve draft SA return summary", btnText: "Review" },
          ]);
        }

      } catch (error) {
        console.error("Error fetching tax year data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTaxData();
  }, [businessId, selectedYearId, taxYears]);

  const handleYearChange = (e) => {
    setSelectedYearId(e.target.value);
  };

  const handleActionClick = (item) => {
    toast.info(`Action clicked for: ${item.text}`);
  };

  const currentYearObj = selectedYearObj || {
    label: "2025/26",
    startDate: "6 Apr 2025",
    endDate: "5 Apr 2026",
    filingDeadline: "31 Jan 2027",
    paymentOnAccount: "31 Jul 2027"
  };

  return (
    <div className="app">
      <Sidebar />
      <main className="main pt-container-bg" style={{background: 'var(--bg)', padding: '0', flex: 1, overflowY: 'auto'}}>
        <DashboardHeader
          title="Personal Tax"
          subtitle="Manage your director self-assessment distributions"
        />
        
        {isLoading && !summary ? (
           <div style={{ height: "60vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
             <CommonLoader />
           </div>
        ) : (
          <div className="personal-tax-page">
            <header className="personal-tax-header-wrap">
              <div className="personal-tax-card" style={{ flex: 1 }}>
                <h1 className="personal-tax-title">Director Self Assessment</h1>
                <div className="personal-tax-legend">
                  <span>Tax year {currentYearObj.label} &mdash; {currentYearObj.startDate} to {currentYearObj.endDate}</span>
                  <span>
                    Filing deadline <strong>{currentYearObj.filingDeadline}</strong> • Payment on account <strong>{currentYearObj.paymentOnAccount}</strong>
                  </span>
                </div>
              </div>
              <div className="personal-tax-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <label style={{ fontSize: '13px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block' }}>
                  Tax year
                </label>
                <select 
                  className="personal-tax-year-select"
                  value={selectedYearId}
                  onChange={handleYearChange}
                >
                  {taxYears.map(year => (
                    <option key={year.id || year._id} value={year.id || year._id}>
                      {year.label || year.taxYearLabel}
                    </option>
                  ))}
                </select>
              </div>
            </header>

            {summary && (
              <section className="pt-metrics-grid">
                <div className="pt-metric-card">
                  <h3>PAYE Salary</h3>
                  <span className="pt-value">£{summary.paye?.salary?.toLocaleString()}</span>
                  <span className="pt-sub">Tax £{summary.paye?.tax?.toLocaleString()} • NIC £{summary.paye?.nic?.toLocaleString()}</span>
                </div>
                <div className="pt-metric-card">
                  <h3>Dividends</h3>
                  <span className="pt-value">£{summary.dividends?.amount?.toLocaleString()}</span>
                  <span className="pt-sub">Allowance remaining £{summary.dividends?.allowanceRemaining?.toLocaleString()}</span>
                </div>
                <div className="pt-metric-card">
                  <h3>Other Income</h3>
                  <span className="pt-value">£{summary.otherIncome?.amount?.toLocaleString()}</span>
                  <span className="pt-sub">{summary.otherIncome?.label}</span>
                </div>
                <div className="pt-metric-card">
                  <h3>Estimated Tax Due</h3>
                  <span className="pt-value">£{summary.estimatedTax?.total?.toLocaleString()}</span>
                  <span className="pt-sub">£{summary.estimatedTax?.split?.toLocaleString()} by 31 Jan • £{summary.estimatedTax?.split?.toLocaleString()} by 31 Jul</span>
                </div>
              </section>
            )}

            <section className="personal-tax-card">
              <div className="pt-flex-between">
                <h2 className="pt-section-title">Key Deadlines</h2>
              </div>
              <div className="pt-deadlines-grid">
                <div className="pt-deadline-item">
                  <strong>31 Jan 2027</strong>
                  <span>Submit SA return & pay balance</span>
                  <span className="pt-badge warning">Pending</span>
                </div>
                <div className="pt-deadline-item">
                  <strong>31 Jul 2027</strong>
                  <span>Second payment on account</span>
                  <span className="pt-badge upcoming">Upcoming</span>
                </div>
                <div className="pt-deadline-item">
                  <strong>Monthly</strong>
                  <span>Review director transactions</span>
                  <span className="pt-badge success">On track</span>
                </div>
              </div>
            </section>

            <section className="personal-tax-card" style={{overflowX: 'auto'}}>
              <h2 className="pt-section-title">Income Breakdown</h2>
              <table className="pt-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Gross</th>
                    <th>Tax Deducted</th>
                    <th>Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, idx) => (
                    <tr key={idx}>
                      <td>{tx.source}</td>
                      <td>£{tx.gross?.toLocaleString()}</td>
                      <td>{tx.taxDeducted ? `£${tx.taxDeducted.toLocaleString()}` : <span>&mdash;</span>}</td>
                      <td>
                        {tx.isLink ? (
                          <a href={tx.evidenceLink || "#"} onClick={(e) => { e.preventDefault(); toast.info("Opening evidence..."); }}>{tx.evidence}</a>
                        ) : (
                          tx.evidence
                        )}
                      </td>
                    </tr>
                  ))}
                  {transactions.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{textAlign: 'center', padding: '24px', color: 'var(--muted)'}}>No income data available for this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="personal-tax-card pt-actions">
              <div className="pt-flex-between" style={{marginBottom: '20px'}}>
                 <h2 className="pt-section-title" style={{margin: 0}}>Outstanding Actions</h2>
                 <button className="pt-action-btn secondary" onClick={() => toast.success("Opening adjustment modal...")}>+ Add Adjustment</button>
              </div>
              
              <ol className="pt-actions-list">
                {checklist.map((item, idx) => (
                  <li key={item.id || idx}>
                    {item.text}
                    {item.btnText && (
                      <button className="pt-action-btn" onClick={() => handleActionClick(item)}>
                        {item.btnText}
                      </button>
                    )}
                  </li>
                ))}
                {checklist.length === 0 && (
                  <li style={{listStyle: 'none', color: 'var(--muted)'}}>You're all caught up! No outstanding actions.</li>
                )}
              </ol>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default PersonalTaxPage;
